/**
 * Product Custom Properties (File Upload, Dropdown, Text Field)
 */
(function() {
  'use strict';

  class ProductCustomPropertiesHandler {
    constructor() {
      this.init();
    }

    init() {
      this.initUploadFields();
      this.initTextFields();
      this.initDropdownFields();
    }

    initUploadFields() {
      const uploadContainers = document.querySelectorAll('[data-custom-upload]');
      uploadContainers.forEach((container) => {
        if (container._uploadInitialized) return;
        container._uploadInitialized = true;

        const maxFiles = parseInt(container.getAttribute('data-max-files'), 10) || 5;
        const propertyName = container.getAttribute('data-property-name') || 'Upload';
        const isRequired = container.getAttribute('data-required') === 'true';
        const productFormId = container.getAttribute('data-product-form-id');

        const dropzone = container.querySelector('[data-upload-dropzone]');
        const nativeInput = container.querySelector('[data-upload-input]');
        const previewsContainer = container.querySelector('[data-upload-previews]');
        const hiddenInputsContainer = container.querySelector('[data-upload-inputs-container]');
        const errorEl = container.querySelector('[data-upload-error]');

        let selectedFiles = [];

        const formatBytes = (bytes) => {
          if (bytes === 0) return '0 B';
          const k = 1024;
          const sizes = ['B', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
        };

        const updateHiddenInputs = () => {
          hiddenInputsContainer.innerHTML = '';
          selectedFiles.forEach((file, index) => {
            const propKey = selectedFiles.length === 1 
              ? `properties[${propertyName}]`
              : `properties[${propertyName} ${index + 1}]`;

            const dt = new DataTransfer();
            dt.items.add(file);

            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.name = propKey;
            if (productFormId) {
              fileInput.setAttribute('form', productFormId);
            }
            fileInput.style.display = 'none';
            fileInput.files = dt.files;
            hiddenInputsContainer.appendChild(fileInput);
          });
        };

        const renderPreviews = () => {
          previewsContainer.innerHTML = '';
          if (selectedFiles.length === 0) {
            previewsContainer.style.display = 'none';
            updateHiddenInputs();
            return;
          }

          previewsContainer.style.display = 'grid';
          selectedFiles.forEach((file, index) => {
            const item = document.createElement('div');
            item.className = 'product-custom-upload__preview-item';

            const isImage = file.type.startsWith('image/');
            let thumbMarkup = '';
            if (isImage) {
              const objUrl = URL.createObjectURL(file);
              thumbMarkup = `<img src="${objUrl}" alt="${file.name}" class="product-custom-upload__preview-thumb">`;
            } else {
              thumbMarkup = `<div class="product-custom-upload__preview-file-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
              </div>`;
            }

            item.innerHTML = `
              ${thumbMarkup}
              <div class="product-custom-upload__preview-meta">
                <span class="product-custom-upload__preview-name" title="${file.name}">${file.name}</span>
                <span class="product-custom-upload__preview-size">${formatBytes(file.size)}</span>
              </div>
              <button type="button" class="product-custom-upload__preview-remove" data-remove-index="${index}" aria-label="Remove file">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            `;

            const removeBtn = item.querySelector('[data-remove-index]');
            removeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              selectedFiles.splice(index, 1);
              renderPreviews();
              clearError();
            });

            previewsContainer.appendChild(item);
          });

          updateHiddenInputs();
        };

        const showError = (msg) => {
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.style.display = 'block';
          }
          dropzone.classList.add('has-error');
        };

        const clearError = () => {
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
          }
          dropzone.classList.remove('has-error');
        };

        const handleFiles = (newFiles) => {
          clearError();
          const fileArr = Array.from(newFiles);
          if (selectedFiles.length + fileArr.length > maxFiles) {
            showError(`You can only select up to ${maxFiles} file(s) in total.`);
            return;
          }

          selectedFiles = selectedFiles.concat(fileArr);
          renderPreviews();
        };

        if (nativeInput) {
          nativeInput.addEventListener('change', (e) => {
            if (e.target.files && e.target.files.length > 0) {
              handleFiles(e.target.files);
              nativeInput.value = '';
            }
          });
        }

        if (dropzone) {
          dropzone.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dropzone.classList.add('is-dragover');
          });

          dropzone.addEventListener('dragleave', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dropzone.classList.remove('is-dragover');
          });

          dropzone.addEventListener('drop', (e) => {
            e.preventDefault();
            e.stopPropagation();
            dropzone.classList.remove('is-dragover');
            if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              handleFiles(e.dataTransfer.files);
            }
          });

          dropzone.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              nativeInput.click();
            }
          });
        }

        container._getSelectedFiles = () => selectedFiles;
        container._validate = () => {
          if (isRequired && selectedFiles.length === 0) {
            showError('Please upload at least one file.');
            return false;
          }
          clearError();
          return true;
        };
      });
    }

    initTextFields() {
      const textContainers = document.querySelectorAll('[data-custom-text]');
      textContainers.forEach((container) => {
        if (container._textInitialized) return;
        container._textInitialized = true;

        const isRequired = container.getAttribute('data-required') === 'true';
        const input = container.querySelector('[data-custom-text-input]');
        const counterCurrent = container.querySelector('[data-char-current]');
        const errorEl = container.querySelector('[data-text-error]');

        const showError = (msg) => {
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.style.display = 'block';
          }
          if (input) input.classList.add('has-error');
        };

        const clearError = () => {
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
          }
          if (input) input.classList.remove('has-error');
        };

        if (input) {
          input.addEventListener('input', () => {
            if (counterCurrent) {
              counterCurrent.textContent = input.value.length;
            }
            if (input.value.trim().length > 0) {
              clearError();
            }
          });
        }

        container._validate = () => {
          if (isRequired && (!input || input.value.trim() === '')) {
            showError('Please fill out this field.');
            return false;
          }
          clearError();
          return true;
        };
      });
    }

    initDropdownFields() {
      const dropdownContainers = document.querySelectorAll('[data-custom-dropdown]');
      dropdownContainers.forEach((container) => {
        if (container._dropdownInitialized) return;
        container._dropdownInitialized = true;

        const isRequired = container.getAttribute('data-required') === 'true';
        const select = container.querySelector('.product-custom-dropdown__select');
        const errorEl = container.querySelector('[data-dropdown-error]');

        const showError = (msg) => {
          if (errorEl) {
            errorEl.textContent = msg;
            errorEl.style.display = 'block';
          }
          if (select) select.classList.add('has-error');
        };

        const clearError = () => {
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.style.display = 'none';
          }
          if (select) select.classList.remove('has-error');
        };

        if (select) {
          select.addEventListener('change', () => {
            if (select.value.trim() !== '') {
              clearError();
            }
          });
        }

        container._validate = () => {
          if (isRequired && (!select || select.value.trim() === '')) {
            showError('Please select an option.');
            return false;
          }
          clearError();
          return true;
        };
      });
    }

    validateAll(context = document) {
      let isValid = true;
      let firstErrorEl = null;

      const uploadContainers = context.querySelectorAll('[data-custom-upload]');
      uploadContainers.forEach((el) => {
        if (el._validate && !el._validate()) {
          isValid = false;
          if (!firstErrorEl) firstErrorEl = el;
        }
      });

      const textContainers = context.querySelectorAll('[data-custom-text]');
      textContainers.forEach((el) => {
        if (el._validate && !el._validate()) {
          isValid = false;
          if (!firstErrorEl) firstErrorEl = el;
        }
      });

      const dropdownContainers = context.querySelectorAll('[data-custom-dropdown]');
      dropdownContainers.forEach((el) => {
        if (el._validate && !el._validate()) {
          isValid = false;
          if (!firstErrorEl) firstErrorEl = el;
        }
      });

      if (!isValid && firstErrorEl) {
        const parentDetails = firstErrorEl.closest('details');
        if (parentDetails && !parentDetails.open) {
          parentDetails.open = true;
        }
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return isValid;
    }
  }

  const customProperties = new ProductCustomPropertiesHandler();
  window.ProductCustomProperties = customProperties;

  document.addEventListener('DOMContentLoaded', () => {
    customProperties.init();
  });

  document.addEventListener('shopify:section:load', () => {
    customProperties.init();
  });
})();
